import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Typography, Paper, Container, Stack, Alert, CircularProgress } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../../config/api';

const ResetPasswordPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!token) {
            setError('Invalid or missing password reset token. Please request a new one.');
        }
    }, [token]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setMessage('');

        if (password !== confirmPassword) {
            return setError('Passwords do not match');
        }

        if (password.length < 6) {
            return setError('Password must be at least 6 characters long');
        }

        setLoading(true);

        try {
            const { data } = await axios.post(getApiUrl('auth/reset-password'), { token, password });
            setMessage(data.message);
            // Auto redirect after 3 seconds
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to reset password. The link may have expired.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#0B0F19',
                backgroundImage: 'radial-gradient(circle at 50% -20%, #1a237e 0%, #0B0F19 70%)'
            }}
        >
            <Container maxWidth="sm">
                <Paper
                    elevation={10}
                    sx={{
                        p: 5,
                        borderRadius: 4,
                        bgcolor: 'rgba(255,255,255,0.05)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.1)'
                    }}
                >
                    <Stack spacing={3} alignItems="center">
                        <Box sx={{ width: 50, height: 50, bgcolor: '#ffab00', borderRadius: 2 }} />
                        <Typography variant="h4" fontWeight="bold" color="white">Secure Reset</Typography>
                        <Typography variant="body2" color="rgba(255,255,255,0.6)" textAlign="center">
                            Please enter your new secure password below.
                        </Typography>

                        {error && (
                            <Alert 
                                severity="error" 
                                sx={{ width: '100%' }}
                                action={
                                    !token && (
                                        <Button color="inherit" size="small" onClick={() => navigate('/forgot-password')}>
                                            Get Link
                                        </Button>
                                    )
                                }
                            >
                                {error}
                            </Alert>
                        )}
                        {message && <Alert severity="success" sx={{ width: '100%' }}>{message} Redirecting to login...</Alert>}

                        {!message && token && (
                            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                                <Stack spacing={3}>
                                    <TextField
                                        label="New Password"
                                        variant="outlined"
                                        fullWidth
                                        required
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        InputLabelProps={{ style: { color: 'rgba(255,255,255,0.7)' } }}
                                        InputProps={{
                                            style: { color: 'white' },
                                            sx: { bgcolor: 'rgba(255,255,255,0.05)' }
                                        }}
                                    />

                                    <TextField
                                        label="Confirm New Password"
                                        variant="outlined"
                                        fullWidth
                                        required
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        InputLabelProps={{ style: { color: 'rgba(255,255,255,0.7)' } }}
                                        InputProps={{
                                            style: { color: 'white' },
                                            sx: { bgcolor: 'rgba(255,255,255,0.05)' }
                                        }}
                                    />

                                    <Button
                                        type="submit"
                                        variant="contained"
                                        size="large"
                                        fullWidth
                                        disabled={loading || !token}
                                        sx={{
                                            bgcolor: '#ffab00',
                                            color: 'black',
                                            fontWeight: 'bold',
                                            py: 1.5,
                                            '&:hover': { bgcolor: '#ffca28' }
                                        }}
                                    >
                                        {loading ? <CircularProgress size={24} /> : 'Update Password'}
                                    </Button>
                                </Stack>
                            </form>
                        )}
                        
                        {(message || !token) && (
                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={() => navigate('/login')}
                                sx={{ 
                                    color: '#ffab00', 
                                    borderColor: '#ffab00',
                                    fontWeight: 'bold',
                                    '&:hover': { borderColor: '#ffca28', color: '#ffca28' }
                                }}
                            >
                                Return to Login
                            </Button>
                        )}
                    </Stack>
                </Paper>
            </Container>
        </Box>
    );
};

export default ResetPasswordPage;
